import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { supabase } from '../../lib/supabase';
import { useRealtimeSubscription } from '../../lib/realtime';
import type { Client, ClientConnection } from '../../types';
import type { SortDirection } from '../../hooks/useSortState';

async function log(biz: string, type: string, desc: string, eId: string, uid: string) {
  try { await supabase.from('activity_log').insert({ business_id: biz, activity_type: type, description: desc, entity_type: 'client', entity_id: eId, logged_by: uid }); } catch { /* */ }
}

export interface ClientRow extends Client {
  booking_count: number;
  invoice_count: number;
  project_count: number;
  invoice_total: number;
}

export interface ClientFilters { search: string; clientType: string | null; source: string | null; }

export interface ClientSummary {
  total: number;
  newThisMonth: number;
  individuals: number;
  companies: number;
  topClient: { name: string; revenue: number } | null;
}

interface UseClientsOptions {
  from: number;
  to: number;
  sortColumn: string | null;
  sortDirection: SortDirection;
}

export function useClients(businessId: string | undefined, options: UseClientsOptions) {
  const [clients, setClients] = useState<ClientRow[]>([]);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [sources, setSources] = useState<string[]>([]);
  const [summary, setSummary] = useState<ClientSummary>({ total: 0, newThisMonth: 0, individuals: 0, companies: 0, topClient: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<ClientFilters>({ search: '', clientType: null, source: null });
  const mountedRef = useRef(true);

  // Debounce search for server-side queries
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(filters.search), 300);
    return () => clearTimeout(t);
  }, [filters.search]);

  const fetch = useCallback(async () => {
    if (!businessId) { setLoading(false); return; }
    setLoading(true); setError(null);
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), 10000);
    try {
      // Build paginated + filtered query
      let query = supabase.from('clients').select('*', { count: 'exact' }).eq('business_id', businessId);
      if (filters.clientType) query = query.eq('client_type', filters.clientType);
      if (filters.source) query = query.eq('source', filters.source);
      if (debouncedSearch) {
        const q = `%${debouncedSearch}%`;
        query = query.or(`name.ilike.${q},email.ilike.${q},phone.ilike.${q},company_name.ilike.${q}`);
      }
      const sortCol = options.sortColumn ?? 'created_at';
      const sortAsc = options.sortColumn ? options.sortDirection === 'asc' : false;
      query = query.order(sortCol, { ascending: sortAsc }).range(options.from, options.to);

      // Summary count queries (non-paginated, head-only)
      const monthStart = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}-01`;
      const [cRes, totalRes, newRes, companyRes, sourcesRes, topInvRes] = await Promise.all([
        query.abortSignal(ctrl.signal),
        supabase.from('clients').select('*', { count: 'exact', head: true }).eq('business_id', businessId).abortSignal(ctrl.signal),
        supabase.from('clients').select('*', { count: 'exact', head: true }).eq('business_id', businessId).gte('created_at', monthStart).abortSignal(ctrl.signal),
        supabase.from('clients').select('*', { count: 'exact', head: true }).eq('business_id', businessId).eq('client_type', 'company').abortSignal(ctrl.signal),
        supabase.from('clients').select('source').eq('business_id', businessId).not('source', 'is', null).abortSignal(ctrl.signal),
        supabase.from('invoices').select('client_name, total').eq('business_id', businessId).not('client_name', 'is', null).abortSignal(ctrl.signal),
      ]);

      if (cRes.error) throw cRes.error;
      const clientData = cRes.data ?? [];
      const clientIds = clientData.map(c => c.id);

      // Aggregation queries scoped to current page
      const bkMap = new Map<string, number>();
      const invMap = new Map<string, { count: number; total: number }>();
      const projMap = new Map<string, number>();
      if (clientIds.length > 0) {
        const [bkRes, invRes, projRes] = await Promise.all([
          supabase.from('bookings').select('client_id').in('client_id', clientIds).abortSignal(ctrl.signal),
          supabase.from('invoices').select('client_id, total').in('client_id', clientIds).abortSignal(ctrl.signal),
          supabase.from('projects').select('client_id').in('client_id', clientIds).abortSignal(ctrl.signal),
        ]);
        for (const b of bkRes.data ?? []) { if (b.client_id) bkMap.set(b.client_id, (bkMap.get(b.client_id) ?? 0) + 1); }
        for (const i of invRes.data ?? []) {
          if (i.client_id) {
            const cur = invMap.get(i.client_id) ?? { count: 0, total: 0 };
            cur.count++; cur.total += (i.total ?? 0);
            invMap.set(i.client_id, cur);
          }
        }
        for (const p of projRes.data ?? []) { if (p.client_id) projMap.set(p.client_id, (projMap.get(p.client_id) ?? 0) + 1); }
      }

      const rows: ClientRow[] = clientData.map(c => ({
        ...c,
        booking_count: bkMap.get(c.id) ?? 0,
        invoice_count: invMap.get(c.id)?.count ?? 0,
        project_count: projMap.get(c.id) ?? 0,
        invoice_total: invMap.get(c.id)?.total ?? 0,
      }));

      // Top client from all invoices
      const revenueByClient = new Map<string, number>();
      for (const inv of topInvRes.data ?? []) {
        if (inv.client_name) revenueByClient.set(inv.client_name, (revenueByClient.get(inv.client_name) ?? 0) + (inv.total ?? 0));
      }
      let topClient: ClientSummary['topClient'] = null;
      for (const [name, revenue] of revenueByClient) {
        if (!topClient || revenue > topClient.revenue) topClient = { name, revenue };
      }

      const allSources = [...new Set((sourcesRes.data ?? []).map((s: { source: string }) => s.source))].sort();
      const totalAll = totalRes.count ?? 0;

      if (mountedRef.current) {
        setClients(rows);
        setTotalCount(cRes.count ?? 0);
        setSources(allSources);
        setSummary({
          total: totalAll,
          newThisMonth: newRes.count ?? 0,
          companies: companyRes.count ?? 0,
          individuals: totalAll - (companyRes.count ?? 0),
          topClient,
        });
      }
    } catch (e) { if (mountedRef.current) setError(e instanceof Error ? e.message : 'Failed'); }
    finally { clearTimeout(t); if (mountedRef.current) setLoading(false); }
  }, [businessId, options.from, options.to, options.sortColumn, options.sortDirection, filters.clientType, filters.source, debouncedSearch]);

  useEffect(() => { mountedRef.current = true; void fetch(); return () => { mountedRef.current = false; }; }, [fetch]);
  const rt = useMemo(() => ['clients'], []);
  useRealtimeSubscription(businessId, rt, fetch);

  const createClient = useCallback(async (data: Partial<Client>, userId: string): Promise<string | null> => {
    if (!businessId) return null;
    try {
      const { data: c, error: e } = await supabase.from('clients').insert({ ...data, business_id: businessId, source: 'manual' }).select('id').maybeSingle();
      if (e || !c) return null;
      await log(businessId, 'client_created', `Client created: ${data.name}`, c.id, userId);
      void fetch(); return c.id;
    } catch { return null; }
  }, [businessId, fetch]);

  const updateClient = useCallback(async (id: string, data: Partial<Client>, userId: string): Promise<boolean> => {
    if (!businessId) return false;
    try {
      const { error: e } = await supabase.from('clients').update({ ...data, updated_at: new Date().toISOString() }).eq('id', id).eq('business_id', businessId);
      if (e) return false;
      await log(businessId, 'client_updated', `Client updated: ${data.name ?? id}`, id, userId);
      void fetch(); return true;
    } catch { return false; }
  }, [businessId, fetch]);

  return { clients, totalCount, sources, loading, error, summary, filters, setFilters, createClient, updateClient, refresh: fetch };
}

// ── Client Connections ──
// Lookup is keyed by users.id (the connecting user), filtered by business_id
// so a multi-business client only surfaces connections to the current business.
// A clients row that has never been connected (user_id IS NULL) returns []
// without burning a query.
export function useClientConnections(userId: string | null, businessId: string) {
  const [connections, setConnections] = useState<ClientConnection[]>([]);
  const mountedRef = useRef(true);
  const fetch = useCallback(async () => {
    if (!userId) { setConnections([]); return; }
    const { data } = await supabase.from('client_connections')
      .select('id, business_id, user_id, connected_via, connected_at, status, code_used, disconnected_at, disconnected_by')
      .eq('user_id', userId).eq('business_id', businessId)
      .order('connected_at', { ascending: false });
    if (mountedRef.current) setConnections((data ?? []) as ClientConnection[]);
  }, [userId, businessId]);
  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(() => { mountedRef.current = true; void fetch(); return () => { mountedRef.current = false; }; }, [fetch]);

  const disconnect = useCallback(async (connId: string, actorUserId: string): Promise<boolean> => {
    try {
      const { error: e } = await supabase.from('client_connections').update({ status: 'disconnected', disconnected_at: new Date().toISOString(), disconnected_by: actorUserId }).eq('id', connId);
      if (e) return false;
      await log(businessId, 'client_disconnected', 'Client connection disconnected', connId, actorUserId);
      void fetch(); return true;
    } catch { return false; }
  }, [businessId, fetch]);

  return { connections, disconnect, refresh: fetch };
}

// ── Client Activity (bookings, invoices, projects) ──
export function useClientActivity(clientId: string | null) {
  const [bookings, setBookings] = useState<{ id: string; date: string; status: string }[]>([]);
  const [invoices, setInvoices] = useState<{ id: string; created_at: string; status: string; total: number }[]>([]);
  const [projects, setProjects] = useState<{ id: string; name: string; status: string }[]>([]);
  const mountedRef = useRef(true);

  const fetch = useCallback(async () => {
    if (!clientId) { setBookings([]); setInvoices([]); setProjects([]); return; }
    const [bk, inv, proj] = await Promise.all([
      supabase.from('bookings').select('id, date, status').eq('client_id', clientId).order('date', { ascending: false }).limit(5),
      supabase.from('invoices').select('id, created_at, status, total').eq('client_id', clientId).order('created_at', { ascending: false }).limit(5),
      supabase.from('projects').select('id, name, status').eq('client_id', clientId).order('created_at', { ascending: false }).limit(5),
    ]);
    if (mountedRef.current) {
      setBookings((bk.data ?? []) as typeof bookings);
      setInvoices((inv.data ?? []) as typeof invoices);
      setProjects((proj.data ?? []) as typeof projects);
    }
  }, [clientId]);
  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(() => { mountedRef.current = true; void fetch(); return () => { mountedRef.current = false; }; }, [fetch]);

  return { bookings, invoices, projects };
}
